import React, { useEffect, useRef } from 'react';
import {
  DragEndEvent,
  TouchSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arraySwap } from '@dnd-kit/sortable';
import axios, { AxiosError, CancelTokenSource } from 'axios';
import { nanoid } from 'nanoid';
import { useTranslation } from 'react-i18next';
import { AdImage } from '@/components/AdImages/AdImages.types';
import { CONFIG } from '@/constants/config';
import { UploadImageResponse } from '@/types';
import { useMobileDetect } from '@/hooks/useMobileDetect';

interface Props {
  images: AdImage[];
  onChange: (images: AdImage[]) => void;
  onError?: () => void;
}

export const useImagesList = ({ images, onChange, onError }: Props) => {
  const imagesRef = useRef(images);
  imagesRef.current = images;
  const cancelSourcesRef = useRef<Record<string, CancelTokenSource>>({});
  const rotatingImageIds = useRef(new Set<string>());
  const isActiveRef = useRef(true);
  const { isDesktop } = useMobileDetect();
  const { i18n } = useTranslation();

  const pointerSensor = useSensor(PointerSensor);
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: { delay: 100, tolerance: 5 },
  });
  const sensors = useSensors(isDesktop ? pointerSensor : touchSensor);

  function update(callback: (prev: AdImage[]) => AdImage[]) {
    if (!isActiveRef.current) return;
    const next = callback(imagesRef.current);
    if (next === imagesRef.current) return;
    imagesRef.current = next;
    onChange(next);
  }

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (over?.id && active.id !== over.id) {
      update(prev => {
        const oldIndex = prev.findIndex(el => el?.id === active.id);
        const newIndex = prev.findIndex(el => el?.id === over.id);
        if (oldIndex < 0 || newIndex < 0) {
          return prev;
        }
        return arraySwap(prev, oldIndex, newIndex);
      });
    }
  }

  const onRemove = (id: string) => {
    cancelSourcesRef.current[id]?.cancel('Operation canceled by the user.');
    delete cancelSourcesRef.current[id];
    update(prev => prev.filter(el => el?.id !== id));
  };

  const handleRotate = (imageId: string, angle: number) => {
    const isImageLoading = imageId.endsWith('-loading');

    if (isImageLoading) return;
    if (rotatingImageIds.current.has(imageId)) return;

    rotatingImageIds.current.add(imageId);
    axios
      .post<{ id: string; thumbnail_url: string }>(
        `https://${CONFIG.photosBaseUrl}/pond/rotate`,
        null,
        {
          params: {
            id: imageId,
            angle,
            lang: i18n.language,
          },
          headers: {
            Accept: 'application/json',
          },
        }
      )
      .then(response => {
        const { id, thumbnail_url: src } = response.data;
        update(prev =>
          prev.map(image => (image?.id === imageId ? { id, src } : image))
        );
      })
      .catch(() => undefined)
      .finally(() => {
        rotatingImageIds.current.delete(imageId);
      });
  };

  const handleUpload = (formData: FormData, imageId: string) => {
    const source = axios.CancelToken.source();
    cancelSourcesRef.current[imageId] = source;
    axios
      .post<UploadImageResponse>(
        `https://${CONFIG.photosBaseUrl}/pond`,
        formData,
        {
          params: { lang: i18n.language },
          cancelToken: source.token,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'multipart/form-data',
          },
        }
      )
      .then(response => {
        delete cancelSourcesRef.current[imageId];
        const [{ id, thumbnail_url: src }] = response.data.photos;
        update(prev => {
          if (!prev.some(item => item?.id === imageId)) {
            return prev;
          }
          return prev.map(item =>
            item?.id === imageId ? { id, src, isUploading: false } : item
          );
        });
      })
      .catch((error: AxiosError) => {
        delete cancelSourcesRef.current[imageId];
        const isStillLoading = imagesRef.current.some(
          image => image?.id === imageId
        );
        if (!isActiveRef.current || axios.isCancel(error) || !isStillLoading) {
          return;
        }
        onError?.();
        update(prev => prev.filter(el => el?.id !== imageId));
      });
  };

  function onFileInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const input = e.target;
    const files = Array.from(input.files ?? []);
    if (!files.length) return;
    const newImages = files.map(file => {
      const tempId = `${nanoid()}-loading`;
      const clonedFile = new File([file], file.name || 'image.jpg', {
        type: file.type || 'image/jpeg',
        lastModified: file.lastModified,
      });
      const formData = new FormData();
      formData.append('images[]', clonedFile);
      handleUpload(formData, tempId);
      return { id: tempId, src: '', isUploading: true };
    });
    input.value = '';
    update(prev => [...prev, ...newImages]);
  }

  useEffect(() => {
    isActiveRef.current = true;
    return () => {
      isActiveRef.current = false;
      Object.values(cancelSourcesRef.current).forEach(source => {
        source.cancel('Operation canceled on unmount.');
      });
      cancelSourcesRef.current = {};
    };
  }, []);

  return {
    sensors,
    onDragEnd,
    onRemove,
    onFileInputChange,
    handleRotate,
  };
};
